import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export interface ApiClientSecretView {
  id: string;
  label: string | null;
  status: string;
  /** ISO timestamp or null. When status='active' and expiresAt is set, the secret is in its rotation overlap window. */
  expiresAt: string | null;
  createdAt: string;
}

export interface ApiClientCertificateView {
  id: string;
  label: string | null;
  status: string;
  fingerprintSha256: string;
  subject: string;
  issuer: string;
  notBefore: string;
  notAfter: string;
  createdAt: string;
  revokedAt: string | null;
}

export interface ApiClientView {
  id: string;
  displayName: string;
  authMethod: string;
  scopes: string[];
  status: string;
  createdAt: string;
  secrets: ApiClientSecretView[];
  certificates: ApiClientCertificateView[];
}

export interface RotatedSecret {
  id: string;
  /** The plaintext value — shown once. The server never returns it again. */
  client_secret: string;
  label: string | null;
  status: string;
}

const KEYS = {
  forIntegration: (orgId: string, productId: string) => ['api-client', orgId, productId] as const,
};

export function useIntegrationApiClient(orgId: string | undefined, productId: string | undefined) {
  return useQuery({
    queryKey: orgId && productId ? KEYS.forIntegration(orgId, productId) : ['api-client', 'idle'],
    queryFn: () =>
      apiGet<ApiClientView | null>(
        `/api/orgs/${orgId}/products/${productId}/integration/api-client`,
      ),
    enabled: !!orgId && !!productId,
  });
}

export function useRotateApiClientSecret(orgId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label?: string; overlap_days?: number }) =>
      apiPost<RotatedSecret>(
        `/api/orgs/${orgId}/products/${productId}/integration/api-client/secrets/rotate`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.forIntegration(orgId, productId) }),
  });
}

export function useRevokeApiClientSecret(orgId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (secretId: string) =>
      apiDelete<void>(
        `/api/orgs/${orgId}/products/${productId}/integration/api-client/secrets/${secretId}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.forIntegration(orgId, productId) }),
  });
}

export interface RegisteredCertificate {
  id: string;
  fingerprintSha256: string;
  notAfter: string;
}

export function useRegisterApiClientCertificate(orgId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { pem: string; label?: string }) =>
      apiPost<RegisteredCertificate>(
        `/api/orgs/${orgId}/products/${productId}/integration/api-client/certificates`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.forIntegration(orgId, productId) }),
  });
}

export interface CreatedApiClient {
  id: string;
  client_secret: string;
  display_name: string;
  status: string;
}

export function useCreateApiClient(orgId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { display_name: string; scopes: string[] }) =>
      apiPost<CreatedApiClient>(
        `/api/orgs/${orgId}/products/${productId}/integration/api-client`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.forIntegration(orgId, productId) }),
  });
}

export function usePatchApiClientScopes(orgId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scopes: string[]) =>
      apiPatch<ApiClientView>(
        `/api/orgs/${orgId}/products/${productId}/integration/api-client/scopes`,
        { scopes },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.forIntegration(orgId, productId) }),
  });
}

export function useRevokeApiClientCertificate(orgId: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (certificateId: string) =>
      apiDelete<void>(
        `/api/orgs/${orgId}/products/${productId}/integration/api-client/certificates/${certificateId}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.forIntegration(orgId, productId) }),
  });
}
